// src/pages/rules/useRulesConfig.js
//
// All state, derived values, and handlers for the SOW Rules Engine page --
// extracted out of RulesConfigPage.jsx (which had grown past 1000 lines
// mixing this with three separate pieces of markup). Nothing here is
// presentational; see RulesTable.jsx / RuleFormModal.jsx / DeleteRuleModal.jsx
// for that, and RulesConfigPage.jsx for how they're assembled.

import { useEffect, useMemo, useState } from "react";
import { fetchRules, createRule, updateRule, deleteRule } from "../../api/sowApi";
import { useAuth, normalizePerms } from "../../context/AuthContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  SOW_RULES_PERMS,
  PAGE_SIZE,
  BASE_CONTRACT_TYPES,
  CATEGORY_OPTIONS,
  DIMENSION_OPTIONS,
  emptyRule,
  severityToImpact,
  clamp14,
} from "./ruleOptions";

export function useRulesConfig() {
  const { hasPermissionAnywhere, hasGlobalPermission, permissionsMap } = useAuth();
  const { accounts } = useWorkspace();

  const canViewPage = hasPermissionAnywhere(SOW_RULES_PERMS);
  const canManageGlobal = hasGlobalPermission("sow:rules:manage_global");
  // An org-wide grant (sow_org_admin) lands in global_permissions, same as
  // any other org-wide role -- see AuthContext's own note on why
  // hasPermission()/hasAnyPermission() check global first.
  const canManageOrgWide = hasGlobalPermission("sow:rules:manage");
  const manageableAccountIds = useMemo(
    () =>
      Object.entries(permissionsMap.accounts || {})
        .filter(([, perms]) => normalizePerms(perms).includes("sow:rules:manage"))
        .map(([id]) => Number(id)),
    [permissionsMap]
  );
  const canCreateAnything = canManageGlobal || canManageOrgWide || manageableAccountIds.length > 0;
  // Accounts the "Account" scope dropdown may offer: every reachable
  // account for an org-wide holder (they may write to any of them), or
  // just the ones explicitly granted for an account-scoped sow_admin.
  const selectableAccounts = useMemo(() => {
    if (canManageOrgWide) return accounts;
    return accounts.filter((a) => manageableAccountIds.includes(a.account_id));
  }, [accounts, canManageOrgWide, manageableAccountIds]);
  const accountNameById = useMemo(() => {
    const m = new Map();
    accounts.forEach((a) => m.set(a.account_id, a.name));
    return m;
  }, [accounts]);

  const canManageRule = (rule) => {
    if (rule.org_id == null) return canManageGlobal;
    if (rule.account_id == null) return canManageOrgWide;
    return canManageOrgWide || manageableAccountIds.includes(rule.account_id);
  };

  const [rules, setRules] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyRule);
  const [scope, setScope] = useState(() =>
    canManageGlobal ? "global" : canManageOrgWide ? "org" : "account"
  );
  const [scopeAccountId, setScopeAccountId] = useState("");

  const [typeMode, setTypeMode] = useState("select");
  const [categoryMode, setCategoryMode] = useState("select");
  const [dimensionMode, setDimensionMode] = useState("select");

  const [customType, setCustomType] = useState("");
  const [categoryCustom, setCategoryCustom] = useState("");
  const [dimensionCustom, setDimensionCustom] = useState("");

  const [filterType, setFilterType] = useState("ALL");
  const [scopeFilter, setScopeFilter] = useState(""); // "" | "global" | "org" | "account"
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await fetchRules({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
        scope: scopeFilter || undefined,
        // "GLOBAL" is passed through as a literal contract_type value on
        // purpose -- no real rule is ever named that, so the backend's own
        // "contract_type == value OR == 'ALL' OR IS NULL" matching (see
        // SqlAlchemyRuleRepository.find_visible()) collapses to exactly
        // "applies to every contract type," which is what this option means.
        contractType: filterType && filterType !== "ALL" ? filterType : undefined,
      });
      setRules(data.items || []);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error(err);
      setError("Failed to load rules.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, scopeFilter, filterType]);

  // Typing a search term should reset back to page 1 -- staying on e.g.
  // page 3 of an unfiltered list after narrowing to 2 results would just
  // show nothing rather than the results just asked for. scopeFilter/
  // filterType reset page inline in their own onChange instead (batched
  // into the same render as the filter change, so this doesn't also fire
  // a second, page-stale fetch the way a separate effect on all three
  // would).
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!message) return;
    const id = setTimeout(() => setMessage(""), 4000);
    return () => clearTimeout(id);
  }, [message]);

  const allContractTypes = useMemo(() => {
    const s = new Set(BASE_CONTRACT_TYPES);
    rules.forEach((r) => {
      if (r.contract_type) s.add(r.contract_type);
    });
    return Array.from(s);
  }, [rules]);

  // Auto set impact from severity (unless user overrides by changing impact manually)
  useEffect(() => {
    setForm((f) => {
      const desired = severityToImpact(f.severity);
      // if impact is empty/null, or equals previous mapping, keep synced
      // (simple rule: always sync impact to severity when auto_weight is ON)
      if (f.auto_weight) {
        return { ...f, impact: desired };
      }
      return f;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.severity]);

  // Auto compute weight from factors when auto_weight is ON
  useEffect(() => {
    if (!form.auto_weight) return;
    const impact = clamp14(form.impact, severityToImpact(form.severity));
    const likelihood = clamp14(form.likelihood, 2);
    const recoverability = clamp14(form.recoverability, 2);
    const applicability = clamp14(form.applicability, 2);
    const raw = impact * likelihood * recoverability * applicability;
    setForm((f) => ({ ...f, weight: raw }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.auto_weight, form.impact, form.likelihood, form.recoverability, form.applicability]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setForm((f) => ({ ...f, [name]: checked }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  const onContractTypeSelectChange = (e) => {
    const value = e.target.value;
    if (value === "__CUSTOM__") {
      setTypeMode("custom");
      setCustomType(
        form.contract_type && !allContractTypes.includes(form.contract_type)
          ? form.contract_type
          : ""
      );
      setFilterType("ALL");
    } else {
      setTypeMode("select");
      setForm((f) => ({ ...f, contract_type: value }));
      setCustomType("");
      setFilterType(value || "ALL");
    }
  };

  const onCustomTypeChange = (e) => {
    const value = e.target.value;
    setTypeMode("custom");
    setCustomType(value);
    setForm((f) => ({ ...f, contract_type: value }));
    setFilterType(value.trim() ? value.trim() : "ALL");
  };

  const contractTypeSelectValue =
    typeMode === "custom" ? "__CUSTOM__" : form.contract_type || "";

  const onCategorySelectChange = (e) => {
    const value = e.target.value;
    if (value === "__CUSTOM__") {
      setCategoryMode("custom");
      setCategoryCustom(
        form.category && !CATEGORY_OPTIONS.includes(form.category)
          ? form.category
          : ""
      );
    } else {
      setCategoryMode("select");
      setForm((f) => ({ ...f, category: value }));
      setCategoryCustom("");
    }
  };

  const onCategoryCustomChange = (e) => {
    const value = e.target.value;
    setCategoryMode("custom");
    setCategoryCustom(value);
    setForm((f) => ({ ...f, category: value }));
  };

  const categorySelectValue =
    categoryMode === "custom" ? "__CUSTOM__" : form.category || "";

  const onDimensionSelectChange = (e) => {
    const value = e.target.value;
    if (value === "__CUSTOM__") {
      setDimensionMode("custom");
      setDimensionCustom(
        form.dimension && !DIMENSION_OPTIONS.includes(form.dimension)
          ? form.dimension
          : ""
      );
    } else {
      setDimensionMode("select");
      setForm((f) => ({ ...f, dimension: value }));
      setDimensionCustom("");
    }
  };

  const onDimensionCustomChange = (e) => {
    const value = e.target.value;
    setDimensionMode("custom");
    setDimensionCustom(value);
    setForm((f) => ({ ...f, dimension: value }));
  };

  const dimensionSelectValue =
    dimensionMode === "custom" ? "__CUSTOM__" : form.dimension || "";

  const onEdit = (rule) => {
    setEditingId(rule.id);

    setForm({
      ...emptyRule,
      ...rule,
      min_score: rule.min_score ?? "",
      max_score: rule.max_score ?? "",
      // default factors if missing in older rules:
      impact: rule.impact ?? severityToImpact(rule.severity),
      likelihood: rule.likelihood ?? 2,
      recoverability: rule.recoverability ?? 2,
      applicability: rule.applicability ?? 2,
      auto_weight: rule.auto_weight ?? false, // keep OFF by default for existing rules
    });

    setTypeMode("select");
    setCustomType("");

    if (rule.category && !CATEGORY_OPTIONS.includes(rule.category)) {
      setCategoryMode("custom");
      setCategoryCustom(rule.category);
    } else {
      setCategoryMode("select");
      setCategoryCustom("");
    }

    if (rule.dimension && !DIMENSION_OPTIONS.includes(rule.dimension)) {
      setDimensionMode("custom");
      setDimensionCustom(rule.dimension);
    } else {
      setDimensionMode("select");
      setDimensionCustom("");
    }

    setError("");
    setMessage("");
    setFormOpen(true);
  };

  const onCancel = () => {
    setEditingId(null);
    setForm(emptyRule);
    setTypeMode("select");
    setCategoryMode("select");
    setDimensionMode("select");
    setCustomType("");
    setCategoryCustom("");
    setDimensionCustom("");
    setScope(canManageGlobal ? "global" : canManageOrgWide ? "org" : "account");
    setScopeAccountId("");
    setError("");
    setMessage("");
    setFormOpen(false);
  };

  const openAddForm = () => {
    onCancel();
    setFormOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!editingId && scope === "account" && !scopeAccountId) {
      setError("Choose an account for this rule's scope.");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");

    const impact = clamp14(form.impact, severityToImpact(form.severity));
    const likelihood = clamp14(form.likelihood, 2);
    const recoverability = clamp14(form.recoverability, 2);
    const applicability = clamp14(form.applicability, 2);

    const computedWeight = impact * likelihood * recoverability * applicability;

    const payload = {
      ...form,
      weight: Number(form.auto_weight ? computedWeight : form.weight) || 0,

      // store factors (backend will compute effective weight from these)
      impact,
      likelihood,
      recoverability,
      applicability,

      min_score: form.min_score === "" ? null : Number(form.min_score),
      max_score: form.max_score === "" ? null : Number(form.max_score),
      contract_type:
        form.contract_type && form.contract_type.trim() !== ""
          ? form.contract_type.trim()
          : null,
    };

    // Scope is set once at creation and never changes on update (the
    // backend's UpdateRuleCommand carries no org_id/account_id at all) --
    // only attach it here, on create.
    if (!editingId) {
      payload.is_global = scope === "global";
      payload.account_id = scope === "account" && scopeAccountId ? Number(scopeAccountId) : null;
    } else {
      delete payload.is_global;
      delete payload.account_id;
      delete payload.org_id;
    }

    try {
      if (editingId) {
        await updateRule(editingId, payload);
        setMessage("Rule updated successfully.");
      } else {
        await createRule(payload);
        setMessage("Rule created successfully.");
      }
      await loadRules();
      onCancel();
    } catch (err) {
      console.error(err);
      setError("Failed to save rule.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    setError("");
    setMessage("");
    try {
      await deleteRule(id);
      await loadRules();
      setMessage("Rule deleted successfully.");
    } catch (err) {
      console.error(err);
      setError("Failed to delete rule.");
    } finally {
      setDeleteTarget(null);
    }
  };

  const availableFilterTypes = useMemo(() => {
    const s = new Set();
    s.add("GLOBAL");
    allContractTypes.forEach((t) => s.add(t));
    return Array.from(s);
  }, [allContractTypes]);

  // Whether an empty result is "the catalog has nothing yet" (show the
  // full-page EmptyState with an Add Rule CTA) vs "this filter matched
  // nothing" (keep the table/toolbar so the user can change the filter --
  // hiding them made the table look like it had vanished).
  const hasActiveFilters = Boolean(debouncedSearch) || Boolean(scopeFilter) || filterType !== "ALL";

  const impact = clamp14(form.impact, severityToImpact(form.severity));
  const likelihood = clamp14(form.likelihood, 2);
  const recoverability = clamp14(form.recoverability, 2);
  const applicability = clamp14(form.applicability, 2);
  const computedWeight = impact * likelihood * recoverability * applicability;
  const normalizedWeight = (computedWeight / 256).toFixed(4);

  const contractTypeOptions = [
    { value: "", label: "GLOBAL (applies to any contract type)" },
    ...allContractTypes.map((t) => ({ value: t, label: t })),
    { value: "__CUSTOM__", label: "+ Custom…" },
  ];
  const categoryOptions = [
    { value: "", label: "Select category…" },
    ...CATEGORY_OPTIONS.map((c) => ({ value: c, label: c })),
    { value: "__CUSTOM__", label: "+ Custom…" },
  ];
  const dimensionOptions = [
    { value: "", label: "Select dimension…" },
    ...DIMENSION_OPTIONS.map((d) => ({ value: d, label: d })),
    { value: "__CUSTOM__", label: "+ Custom…" },
  ];
  const filterOptions = [
    { value: "ALL", label: "All" },
    { value: "GLOBAL", label: "GLOBAL" },
    ...availableFilterTypes.filter((t) => t !== "GLOBAL").map((t) => ({ value: t, label: t })),
  ];
  const scopeSelectOptions = [
    ...(canManageGlobal ? [{ value: "global", label: "Global (platform-wide baseline)" }] : []),
    ...(canManageOrgWide ? [{ value: "org", label: "Org-wide (everyone in your org)" }] : []),
    ...(canManageOrgWide || selectableAccounts.length > 0
      ? [{ value: "account", label: "One account" }]
      : []),
  ];

  return {
    // permissions / scope capability
    canViewPage,
    canManageGlobal,
    canManageOrgWide,
    canCreateAnything,
    manageableAccountIds,
    selectableAccounts,
    accountNameById,
    canManageRule,

    // list state
    rules,
    loading,
    error,
    message,
    page,
    totalPages,
    setPage,
    hasActiveFilters,

    // list filters
    searchInput,
    setSearchInput,
    scopeFilter,
    setScopeFilter,
    filterType,
    setFilterType,
    filterOptions,

    // create/edit form
    formOpen,
    editingId,
    form,
    onChange,
    scope,
    setScope,
    scopeAccountId,
    setScopeAccountId,
    scopeSelectOptions,
    typeMode,
    customType,
    contractTypeSelectValue,
    contractTypeOptions,
    onContractTypeSelectChange,
    onCustomTypeChange,
    categoryMode,
    categoryCustom,
    categorySelectValue,
    categoryOptions,
    onCategorySelectChange,
    onCategoryCustomChange,
    dimensionMode,
    dimensionCustom,
    dimensionSelectValue,
    dimensionOptions,
    onDimensionSelectChange,
    onDimensionCustomChange,
    computedWeight,
    normalizedWeight,
    saving,
    onSubmit,
    onEdit,
    onCancel,
    openAddForm,

    // delete
    deleteTarget,
    setDeleteTarget,
    onDelete,
  };
}
